import * as React from "react";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

/**
 * Props for the ConfirmDialog component
 */
export interface ConfirmDialogProps {
  /**
   * Whether the dialog is open
   */
  open: boolean;

  /**
   * Callback fired when the open state changes
   */
  onOpenChange: (open: boolean) => void;

  /**
   * Callback fired when the user confirms the action
   * Can be async - dialog will stay open until Promise resolves
   */
  onConfirm: () => void | Promise<void>;

  /** Whether the caller's action is still running. */
  isPending?: boolean;

  /** Message shown when confirmation fails, allowing another attempt. */
  errorText?: string;

  /**
   * The title of the dialog
   */
  title: string;

  /**
   * The description/message of the dialog
   */
  description: string;

  /**
   * The text for the confirm button
   * @default "Delete"
   */
  confirmText?: string;

  /**
   * The text for the cancel button
   * @default "Cancel"
   */
  cancelText?: string;

  /**
   * The variant of the confirm button
   * @default "destructive"
   */
  confirmVariant?: "default" | "destructive";

  /**
   * Additional CSS class for the dialog content
   */
  className?: string;
}

/**
 * ConfirmDialog - A reusable confirmation dialog component
 *
 * Built on shadcn/ui AlertDialog for consistent UX.
 * Replaces blocking window.confirm() calls with proper AlertDialog.
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 *
 * <ConfirmDialog
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   onConfirm={() => {
 *     // Handle delete
 *     setIsOpen(false);
 *   }}
 *   title="Delete Document"
 *   description="Are you sure you want to delete this document? This action cannot be undone."
 * />
 * ```
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending = false,
  errorText = "Could not complete this action. Please try again.",
  title,
  description,
  confirmText = "Delete",
  cancelText = "Cancel",
  confirmVariant = "destructive",
  className,
}: ConfirmDialogProps): React.ReactElement {
  const [isConfirming, setIsConfirming] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const confirming = React.useRef(false);
  const busy = isPending || isConfirming;
  const handleConfirm = React.useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      if (isPending || confirming.current) return;
      confirming.current = true;
      setIsConfirming(true);
      setHasError(false);
      try {
        await onConfirm();
        onOpenChange(false);
      } catch {
        setHasError(true);
      } finally {
        confirming.current = false;
        setIsConfirming(false);
      }
    },
    [onConfirm, onOpenChange, isPending]
  );

  const handleOpenChange = (nextOpen: boolean) => {
    if (isPending || confirming.current) return;
    setHasError(false);
    onOpenChange(nextOpen);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className={cn(className)} aria-busy={busy}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {hasError && (
          <p role="alert" className="text-sm text-destructive">
            {errorText}
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>{cancelText}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={busy}
            aria-busy={busy}
            className={cn(
              confirmVariant === "destructive" && "bg-destructive hover:bg-destructive/90"
            )}
          >
            {busy && <Loader2 className="mr-2 size-4 animate-spin" aria-hidden="true" />}
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
